import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {MessageBusService} from '../message-bus.service';
import {SessionService} from '../authentication/session-service.service';
import {AccessControlService} from '../access-control-service.class';
import {UserSettingsService} from '../user-settings.service';
import {
    ACTION_BOARD_CHANGED_MESSAGE_TYPE,
    AcknowledgeActionItem,
    ActionBoardChangedDto,
    ActionBoardSubjectWireDto,
    ActionBoardUserWireDto,
    ActionItemEventWireDto,
    ActionItemWireDto,
    ActionSeverity,
    CancelScheduledActionNotice,
    ALL_USERS_KEY,
    GetActionBoard,
    GetActionBoardArchive,
    GetActionBoardArchiveReply,
    GetActionBoardReply,
    GetActionBoardSubjects,
    GetActionBoardSubordinates,
    GetActionItemRecord,
    GetOutstandingActionItemsByPath,
    GetScheduledActionNotices,
    MarkActionItemRead,
    MarkActionItemUnread,
    MarkActionItemsDelivered,
    PostActionNotice,
    ScheduledNoticeWireDto,
    WithdrawActionNotice
} from '../shell-communication/action-board-operations';
import {
    highestSeverity,
    isOutstanding,
    OPEN_SUBJECT_REQUEST_TOPIC,
    OpenSubjectRequest,
    overdueDays,
    parsePayload,
    subjectPortfolioId
} from './action-board.model';

/** What the overlay needs: the viewed client's full outstanding set, summarized (§5.7). */
export interface OutstandingSummary {
    userId: string;
    outstanding: number;
    overdue: number;
    highest: ActionSeverity | '';
}

/** CR-20260924-001 F10: the login read retries at these gaps, then gives up (owner 2026-09-24). */
const INITIAL_LOAD_RETRY_MS = [2000, 5000, 15000];

/**
 * CR-20260927-001 — the super user's two switches (owner screencast 2026-09-27). A user setting, so
 * they survive re-login (owner ruling 2). Defaults (owner ruling 4): overlay OFF, messages ON.
 * They bind SUPER USERS ONLY: for anyone else the stored value is ignored and both read as ON.
 */
export interface SuperUserPreferences {
    /** The colour wash over the other panels. */
    overlayEnabled: boolean;
    /** Incoming items on the OWN board. OFF = the board is muted on the client (owner ruling 1a / 3):
     *  the Shell still originates and delivers, nothing is shown and the wash stays off; ON again = everything shows. */
    messagesEnabled: boolean;
}

const SUPER_USER_PREFERENCES_KEY = 'action-board.super-user-preferences';
const DEFAULT_SUPER_USER_PREFERENCES: SuperUserPreferences = {overlayEnabled: false, messagesEnabled: true};

/** The panel secure-element grants a deep link needs (the same GUIDs the workspace security context uses). */
const PANEL_GRANTS: { [panel: string]: string } = {
    APG: '184f0d77-b9a4-46d7-adee-0f448ed7614d',
    HedgingGrid: '465289b5-6930-41e1-8d57-5bd8d0b2ff9f',
    // CR-20260930-001 M4 (owner): an analysis overdue card opens COLLARS - the acquisition
    // happens there - with the combo auto-selected.
    Collars: '14146f1c-9502-4f57-9870-a97900d1953b'
};

/**
 * CR-20260922-001 — the board's client-side hub. Root singleton: the overlay, the panel, the composer
 * and the panel banners all read it. It holds the OWN summary for the overlay (from the board load
 * and from every ActionBoardChanged push).
 *
 * CR-20260924-001 (owner rulings 2026-09-24):
 *   F1  — Delivered = the item was loaded into the recipient's OWN session (he was online after it
 *         originated). Written HERE, by every own-board load — the login read included — never by
 *         the board panel rendering a card. Read (card expanded) stays the panel's.
 *   F3  — the overlay reflects the OWN outstanding set only; viewing a subordinate's board never
 *         changes the wash ("the wash should only be visible for own events, not clients").
 *   F4a — a deep link is offered only when the target panel's secure element is granted.
 *   F10 — the login read retries with back-off.
 */
@Injectable({providedIn: 'root'})
export class ActionBoardService {

    constructor(
        private readonly _shellClient: ShellClientService,
        private readonly _messageBus: MessageBusService,
        private readonly _session: SessionService,
        private readonly _accessControl: AccessControlService,
        private readonly _userSettings: UserSettingsService
    ) {
        this._messageBus.of<ActionBoardChangedDto>(ACTION_BOARD_CHANGED_MESSAGE_TYPE)
            .subscribe(msg => this.onBoardChanged(msg.payload));
    }

    private readonly _ownSummary$ = new BehaviorSubject<OutstandingSummary | null>(null);
    private readonly _boardChanged$ = new Subject<ActionBoardChangedDto>();
    private readonly _preferences$ = new BehaviorSubject<SuperUserPreferences | null>(null);

    // ---------------------------------------------------------------- super user preferences (CR-20260927-001)

    /** The stored switches (defaults filled in). Read lazily: the user settings arrive with the login. */
    get preferences(): SuperUserPreferences {
        if (!this._preferences$.value) {
            const stored = this._userSettings.getValue<Partial<SuperUserPreferences>>(SUPER_USER_PREFERENCES_KEY) || {};
            this._preferences$.next({...DEFAULT_SUPER_USER_PREFERENCES, ...stored});
        }
        return this._preferences$.value;
    }

    /** Emits on every change of the switches (and once on first read). */
    get preferences$(): Observable<SuperUserPreferences | null> {
        return this._preferences$;
    }

    /** Effective: the wash may paint. Non-super users always. */
    get overlayEnabled(): boolean {
        return !this.isSuperUser || this.preferences.overlayEnabled;
    }

    /** Effective: incoming items show on the OWN board. Non-super users always. */
    get messagesEnabled(): boolean {
        return !this.isSuperUser || this.preferences.messagesEnabled;
    }

    /** Super user only; stores the user setting and republishes. The caller reloads the board. */
    async setPreferences(next: SuperUserPreferences): Promise<void> {
        if (!this.isSuperUser) {
            return;
        }
        const value: SuperUserPreferences = {...next};
        this._preferences$.next(value);
        await this._userSettings.setValue(SUPER_USER_PREFERENCES_KEY, value);
    }

    /** The logged-in user's own outstanding summary; null until the first load. */
    get ownSummary$(): Observable<OutstandingSummary | null> {
        return this._ownSummary$;
    }

    get ownSummary(): OutstandingSummary | null {
        return this._ownSummary$.value;
    }

    /** Every board-changed push, for any user this session receives them for. */
    get boardChanged$(): Observable<ActionBoardChangedDto> {
        return this._boardChanged$;
    }

    get ownUserId(): string {
        return this._session.sessionData?.userId;
    }

    // ---------------------------------------------------------------- reads

    async loadBoard(viewedUserId: string | null): Promise<GetActionBoardReply> {
        const allUsers = viewedUserId === ALL_USERS_KEY;
        const reply = await this._shellClient.processQuery<GetActionBoardReply>(
            allUsers ? new GetActionBoard(null, true) : new GetActionBoard(viewedUserId || null));
        reply.items = reply.items || [];

        if (allUsers) {
            // CR-20260929-002: the All view carries the caller's own items too. They were loaded into
            // the own session, so the own-board rules apply to THEM only: Delivered (F1), the Messages
            // mute (CR-20260927-001), the own summary. Subordinates' items are untouched.
            const own = reply.items.filter(x => x.recipientUserId === this.ownUserId);
            await this.recordDelivered(own);

            if (!this.messagesEnabled) {
                reply.items = reply.items.filter(x => x.recipientUserId !== this.ownUserId);
                this._ownSummary$.next(this.summarize(this.ownUserId, [], reply.todayDayKey));
            } else {
                this._ownSummary$.next(this.summarize(this.ownUserId, own, reply.todayDayKey));
            }
            return reply;
        }

        if (reply.isOwnBoard) {
            // F1: loaded into the own session = delivered. Once per item; the reply is patched so a
            // second load before the push does not resend. Written BEFORE the mute below: a muted
            // board is still a session the item was loaded into (CR-20260927-001, told to the owner).
            await this.recordDelivered(reply.items);

            if (!this.messagesEnabled) {
                // CR-20260927-001: Messages OFF = the own board is muted on the client. Nothing is
                // shown, the summary is empty so the wash stays off. Subordinates' boards are untouched.
                reply.items = [];
            }

            this._ownSummary$.next(this.summarize(reply.viewedUserId, reply.items, reply.todayDayKey));
        }

        return reply;
    }

    /**
     * F10 — the login read (the overlay's, §5.7 "evaluated on login"): one attempt, then retries at
     * 2 s, 5 s, 15 s; each failure logged once. Stops early when the own summary arrived by another
     * road (a push, a board panel's load). Never throws.
     */
    async loadOwnBoardWithRetry(): Promise<void> {
        const gaps = [0, ...INITIAL_LOAD_RETRY_MS];
        for (let attempt = 0; attempt < gaps.length; attempt++) {
            if (gaps[attempt] > 0) {
                await new Promise<void>(resolve => setTimeout(resolve, gaps[attempt]));
                if (this._ownSummary$.value) {
                    return;   // arrived meanwhile
                }
            }
            try {
                await this.loadBoard(null);
                return;
            } catch (e) {
                const last = attempt === gaps.length - 1;
                console.error(`[action-board] initial load failed (attempt ${attempt + 1} of ${gaps.length}${last ? ', giving up' : ''})`, e);
            }
        }
    }

    private async recordDelivered(items: ActionItemWireDto[]): Promise<void> {
        const ids = items
            .filter(x => !x.completedAtUtc && !x.isStale && !x.deliveredAtUtc)
            .map(x => x.actionItemId);

        if (ids.length === 0) {
            return;
        }

        try {
            await this.markDelivered(ids);
            const now = new Date().toISOString();
            for (const item of items) {
                if (ids.indexOf(item.actionItemId) >= 0) {
                    item.deliveredAtUtc = now;
                }
            }
        } catch (e) {
            console.error('[action-board] delivered failed', e);
        }
    }

    /** The Archive face: one server-filtered page of every completed item (owner 2026-09-23). */
    async loadArchive(query: GetActionBoardArchive): Promise<GetActionBoardArchiveReply> {
        const reply = await this._shellClient.processQuery<GetActionBoardArchiveReply>(query);
        reply.items = reply.items || [];
        reply.portfolios = reply.portfolios || [];
        return reply;
    }

    async loadRecord(actionItemId: string): Promise<ActionItemEventWireDto[]> {
        return (await this._shellClient.processQuery<ActionItemEventWireDto[]>(new GetActionItemRecord(actionItemId))) || [];
    }

    async loadSubordinates(): Promise<ActionBoardUserWireDto[]> {
        return (await this._shellClient.processQuery<ActionBoardUserWireDto[]>(new GetActionBoardSubordinates())) || [];
    }

    async loadSubjects(userId: string | null): Promise<ActionBoardSubjectWireDto[]> {
        return (await this._shellClient.processQuery<ActionBoardSubjectWireDto[]>(new GetActionBoardSubjects(userId))) || [];
    }

    async loadOutstandingByPath(userId: string | null, pathPrefix: string): Promise<ActionItemWireDto[]> {
        return (await this._shellClient.processQuery<ActionItemWireDto[]>(new GetOutstandingActionItemsByPath(userId, pathPrefix))) || [];
    }

    // ---------------------------------------------------------------- writes (own session only)

    async markDelivered(actionItemIds: string[]): Promise<void> {
        if (!actionItemIds || actionItemIds.length === 0) {
            return;
        }
        await this._shellClient.processCommand(new MarkActionItemsDelivered(actionItemIds));
    }

    async markRead(actionItemId: string): Promise<void> {
        await this._shellClient.processCommand(new MarkActionItemRead(actionItemId));
    }

    /** The recipient's own item only (the backend refuses anyone else). */
    async markUnread(actionItemId: string): Promise<void> {
        await this._shellClient.processCommand(new MarkActionItemUnread(actionItemId));
    }

    get isSuperUser(): boolean {
        return !!this._session.isSuperUser;
    }

    async acknowledge(actionItemId: string, evidence: string | null = null): Promise<void> {
        await this._shellClient.processCommand(new AcknowledgeActionItem(actionItemId, evidence));
    }

    /**
     * Opening the owning panel from an outstanding ExpiringToday card IS the acknowledgement (owner
     * 2026-09-23): the user has looked at the expiring legs and may let them expire. Notices keep
     * their Acknowledge button; ZoneDrift stays observable only. The caller checks it is the
     * recipient's OWN board - a manager's click is inert.
     */
    acknowledgesByOpening(item: ActionItemWireDto): boolean {
        return item.itemType === 'ExpiringToday' && isOutstanding(item) && !!item.openPanel;
    }

    async postNotice(cmd: PostActionNotice): Promise<void> {
        await this._shellClient.processCommand(cmd);
    }

    async withdrawNotice(noticeId: string): Promise<void> {
        await this._shellClient.processCommand(new WithdrawActionNotice(noticeId));
    }

    // ---------------------------------------------------------------- scheduled alerts (owner 2026-09-23)

    /** The caller's own alerts waiting to be sent, soonest first. */
    async loadScheduledNotices(): Promise<ScheduledNoticeWireDto[]> {
        return (await this._shellClient.processQuery<ScheduledNoticeWireDto[]>(new GetScheduledActionNotices())) || [];
    }

    async cancelScheduledNotice(scheduledNoticeId: string): Promise<void> {
        await this._shellClient.processCommand(new CancelScheduledActionNotice(scheduledNoticeId));
    }

    // ---------------------------------------------------------------- board on screen

    private readonly _visibleBoards = new Map<string, string>(); // panelId -> layoutTabId
    private readonly _visibleBoards$ = new BehaviorSubject<number>(0);

    /** A board panel reports whether it is the active tab of its workspace (and which layout tab holds it). */
    reportBoardVisible(panelId: string, layoutTabId: string, isActive: boolean): void {
        if (isActive) {
            this._visibleBoards.set(panelId, layoutTabId);
        } else {
            this._visibleBoards.delete(panelId);
        }
        this._visibleBoards$.next(this._visibleBoards.size);
    }

    /** True when an active board panel sits in the given layout tab: the wash steps aside for it (owner 2026-09-22). */
    isBoardOnScreen(activeLayoutTabId: string | null): boolean {
        if (!activeLayoutTabId) {
            return this._visibleBoards.size > 0;
        }
        return Array.from(this._visibleBoards.values()).some(tab => tab === activeLayoutTabId);
    }

    get boardVisibility$(): Observable<number> {
        return this._visibleBoards$;
    }

    // ---------------------------------------------------------------- deep link

    /** F4a: is the panel a card would open granted to this user? Unknown panel = not granted. */
    isPanelGranted(panel: string): boolean {
        const guid = PANEL_GRANTS[panel];
        return !!guid && this._accessControl.isSecureElementAvailable(guid);
    }
    isItemOpenGranted(item: ActionItemWireDto): boolean {
        return !!item?.openPanel && this.isPanelGranted(item.openPanel)
            && (item.itemType !== 'AvailableAdjustment' || (
                this._accessControl.isSecureElementAvailable('3ffd6a93-0d5a-4688-80ce-1c4d3d730043')
                && this._accessControl.isSecureElementAvailable('9931888b-4320-49b8-a0ce-951c897f5f40')));
    }

    /** Ask the host to open the item's source panel on its subject (§4.2). False = nothing to open, or not granted. */
    openSubject(item: ActionItemWireDto, sourceLayoutTabId: string): boolean {
        if (item.cardAction !== 'open-source-panel' && item.openPanel === '') {
            return false;
        }
        const panel = item.openPanel;
        const portfolioId = subjectPortfolioId(item.subjectPath);
        if (!panel || !portfolioId || !this.isItemOpenGranted(item)) {
            return false;
        }
        const payload = parsePayload(item);
        const request: OpenSubjectRequest = {
            itemType: item.itemType,
            panel,
            userId: item.recipientUserId,
            portfolioId,
            // CR-20260930-001 M4: an analysis card names the Collars COMBO to load.
            comboId: payload.comboId || null,
            expirationDayKey: payload.expirationDayKey || null,
            actionItemId: item.actionItemId,
            targetPanelId: null,
            sourceLayoutTabId: panel === 'APG' || panel === 'HedgingGrid' ? sourceLayoutTabId : undefined,
            // CR-20260927-003: a zone-drift card carries the market zone; APG prices the move into it.
            zoneDrift: item.itemType === 'ZoneDrift' && typeof payload.zone === 'number' && isFinite(payload.zone)
                ? {zone: payload.zone}
                : null,
            at: Date.now()
        };
        console.log(`[action-board] open subject: panel=${panel} portfolio=${portfolioId} itemType=${item.itemType}`
            + ` payload.zone=${payload.zone} (${typeof payload.zone}) -> zoneDrift=${JSON.stringify(request.zoneDrift)}`);
        this._messageBus.publish({topic: OPEN_SUBJECT_REQUEST_TOPIC, payload: request});
        return true;
    }

    // ---------------------------------------------------------------- internals

    summarize(userId: string, items: ActionItemWireDto[], todayDayKey: string): OutstandingSummary {
        const outstanding = items.filter(isOutstanding);
        return {
            userId,
            outstanding: outstanding.length,
            overdue: outstanding.filter(x => overdueDays(x, todayDayKey) > 0).length,
            highest: highestSeverity(items)
        };
    }

    private onBoardChanged(dto: ActionBoardChangedDto): void {
        if (!dto) {
            return;
        }

        if (dto.userId === this.ownUserId) {
            // CR-20260927-001: a muted own board reports nothing outstanding, whatever the push says.
            const muted = !this.messagesEnabled;
            this._ownSummary$.next({
                userId: dto.userId,
                outstanding: muted ? 0 : dto.outstandingCount,
                overdue: muted ? 0 : dto.overdueCount,
                highest: muted ? '' : dto.highestSeverity
            });
        }

        this._boardChanged$.next(dto);
    }
}
